/**
 * Portal overlay (ADR-0023, evolves the ADR-0010 S5 drawer) — a slide-over
 * (`mode: "drawer"`) OR a centered modal (`mode: "modal"`) that hosts a *target
 * page* (fetched fresh) with its own nested runtime.
 *
 * Two ways it is opened, both pushing onto the runtime's overlay stack:
 *  - a `data_list` row action seeds `vars` (e.g. `{ row: <id> }`) merged into
 *    the overlay page's `$page` (the original Airtable-style record drawer);
 *  - an `open_overlay` action seeds `input` as the read-only `$input` namespace
 *    and may pass a `result` back to the opener's `on_close` chain.
 *
 * `onClose(result?)` pops this overlay from the stack; the runtime then runs the
 * opener's `on_close` (with `$result` bound) and refreshes the opener so an
 * edit/create done inside is reflected. It renders a NESTED `PageRuntimeProvider`
 * so a block inside the overlay uses the overlay's own refresh + can self-close.
 */
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";

import { portalApi } from "@/lib/api-portal";

import { PageRenderer } from "./PageRenderer";
import { PageRuntimeProvider } from "./PageRuntime";

export function PortalOverlay({
  appSlug,
  pageSlug,
  mode,
  vars,
  input,
  onClose,
}: {
  appSlug: string;
  pageSlug: string;
  /** `drawer` = right slide-over; `modal` = centered dialog. */
  mode: "modal" | "drawer";
  /** Seed vars merged into the overlay page's `$page` (record drawer). */
  vars: Record<string, unknown>;
  /** Read-only `$input` namespace (open_overlay context). */
  input: Record<string, unknown>;
  /** Close this overlay, optionally returning a `result` map to the opener. */
  onClose: (result?: Record<string, unknown>) => void;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["portal-page", appSlug, pageSlug, "overlay"],
    queryFn: () => portalApi.getPage(appSlug, pageSlug),
    retry: false,
  });

  const body = (
    <>
      <button
        type="button"
        onClick={() => onClose()}
        aria-label="Close"
        className="absolute right-4 top-4 rounded-[var(--portal-radius)] p-1 text-muted-foreground hover:bg-muted"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-danger">Could not open.</p>}
      {data && (
        <PageRuntimeProvider
          appSlug={appSlug}
          result={data}
          refresh={() => void refetch()}
          initialVars={vars}
          input={input}
          onClose={onClose}
        >
          <PageRenderer result={data} />
        </PageRuntimeProvider>
      )}
    </>
  );

  // `tray-light` pins the portal light tokens even though the overlay may mount
  // outside the shell subtree (so `bg-surface` is the bright panel).
  return (
    <div
      className={`tray-light fixed inset-0 z-50 flex ${
        mode === "modal" ? "items-center justify-center p-4" : "justify-end"
      }`}
    >
      {/* Backdrop — a soft cool dim (not a muddy grey veil). */}
      <div
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[1px]"
        onClick={() => onClose()}
        aria-hidden
      />
      {mode === "modal" ? (
        <div
          className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-[var(--portal-radius)] border border-border bg-surface p-6 shadow-xl"
          role="dialog"
          aria-modal="true"
        >
          {body}
        </div>
      ) : (
        <aside
          className="relative h-full w-full max-w-xl overflow-y-auto border-l border-border bg-surface p-6 shadow-xl"
          role="dialog"
          aria-modal="true"
        >
          {body}
        </aside>
      )}
    </div>
  );
}
